import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useContent } from '@/context/ContentContext';
import { ArrowUpRight } from 'lucide-react';

export default function Contact() {
  const { t } = useContent();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please complete the required fields.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/messages', form);
      toast.success(t.contact.success);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!t) return null;

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 sm:py-24 md:py-40 bg-paper">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-ember mb-6 sm:mb-8">
            § Chapter VI · Contact
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight mb-4 sm:mb-6">
            {t.contact.title}
          </h2>
          <p className="text-ink-dim text-base md:text-lg leading-relaxed max-w-md mb-8 sm:mb-12 italic font-serif">
            {t.contact.subtitle}
          </p>

          <dl className="space-y-4 sm:space-y-6 font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] sm:tracking-[0.25em]">
            <div className="flex items-start justify-between gap-3 border-t border-[var(--line)] pt-4 sm:pt-6">
              <dt className="text-ink-dim flex-shrink-0">{t.contact.office_label}</dt>
              <dd className="text-ink text-right break-words">{t.contact.office_value}</dd>
            </div>
            <div className="flex items-start justify-between gap-3 border-t border-[var(--line)] pt-4 sm:pt-6">
              <dt className="text-ink-dim flex-shrink-0">{t.contact.email_contact_label}</dt>
              <dd className="text-ember text-right break-all">{t.contact.email_contact_value}</dd>
            </div>
          </dl>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7 bg-paper-2 border border-[var(--line)] p-6 sm:p-8 md:p-12 clip-frame"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Field
              label={t.contact.name_label}
              value={form.name}
              onChange={onChange('name')}
              testid="contact-name"
            />
            <Field
              label={t.contact.email_label}
              type="email"
              value={form.email}
              onChange={onChange('email')}
              testid="contact-email"
            />
          </div>
          <div className="mb-6">
            <Field
              label={t.contact.subject_label}
              value={form.subject}
              onChange={onChange('subject')}
              testid="contact-subject"
            />
          </div>
          <div className="mb-10">
            <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
              {t.contact.message_label}
            </label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={onChange('message')}
              rows={6}
              className="w-full bg-transparent border-b border-[var(--line-strong)] focus:border-ember focus:outline-none py-3 text-ink text-base font-serif resize-none transition-colors"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-ember disabled:opacity-60"
          >
            {loading ? '…sending' : t.contact.submit}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[var(--line-strong)] focus:border-ember focus:outline-none py-3 text-ink text-base font-serif transition-colors"
        required
      />
    </div>
  );
}
